import { MedicineSimple } from '@dreryk/drerykweb-medicine-api';
import {
   DosageBreak,
   DosagePeriod,
   DosageStage,
   DosageStageUnion,
   DosageTimeUnit,
   ExtendedDosage,
   FrequencyDosageCycle,
   TimesOfDayDosageCycle,
} from '@dreryk/egabinet-prescription-published-language-api';

import { hasCompositePackage, hasKnownPackageCapacity } from './medicineSufficiency';
import { getQuantityMaxValue } from './quantity';
import { getTreatmentDaysCountMaxValue } from './treatmentDaysCount';

const HOURS_PER_DAY = 24;

const HOURS_PER_UNIT: Record<DosageTimeUnit, number> = {
   HOUR: 1,
   DAY: 24,
   WEEK: 168,
   MONTH: 720,
};

export const isFrequencyDosageCycle = (stage: DosageStageUnion): stage is FrequencyDosageCycle => {
   return stage.class === 'FrequencyDosageCycle';
};

export const isTimesOfDayDosageCycle = (stage: DosageStageUnion): stage is TimesOfDayDosageCycle => {
   return stage.class === 'TimesOfDayDosageCycle';
};

export const isDosageBreak = (stage: DosageStageUnion): stage is DosageBreak => {
   return stage.class === 'DosageBreak';
};

const isDosageCycle = (stage: DosageStageUnion): stage is FrequencyDosageCycle | TimesOfDayDosageCycle => {
   return isFrequencyDosageCycle(stage) || isTimesOfDayDosageCycle(stage);
};

const getPeriodInHours = (period: DosagePeriod): number => {
   return period.amount * HOURS_PER_UNIT[period.unit];
};

export const getPeriodInDays = (period: DosagePeriod): number => {
   return getPeriodInHours(period) / HOURS_PER_DAY;
};

export const getStageDurationDays = (period: DosagePeriod): number => {
   return Math.max(Math.ceil(getPeriodInDays(period)), 1);
};

const getAdministrationsCount = (duration: number, interval: number): number => {
   return Math.max(Math.floor(duration / interval), 1);
};

const getFrequencyCycleAdministrationsCount = (cycle: FrequencyDosageCycle): number => {
   const intervalHours = getPeriodInHours(cycle.interval);

   if (!intervalHours) {
      return 0;
   }

   return getAdministrationsCount(getPeriodInHours(cycle.duration), intervalHours);
};

const getTimesOfDayCycleDailyDose = (cycle: TimesOfDayDosageCycle): number => {
   return cycle.portions.reduce((sum, { portion }) => sum + portion, 0);
};

const getCycleAdministrationDays = (cycle: FrequencyDosageCycle | TimesOfDayDosageCycle): number => {
   const durationDays = getPeriodInDays(cycle.duration);

   if (isTimesOfDayDosageCycle(cycle) && cycle.administrationIntervalDays > 0) {
      return getAdministrationsCount(durationDays, cycle.administrationIntervalDays);
   }

   return durationDays;
};

const getCycleMedicineDemand = (cycle: FrequencyDosageCycle | TimesOfDayDosageCycle): number => {
   if (isFrequencyDosageCycle(cycle)) {
      return cycle.portion * getFrequencyCycleAdministrationsCount(cycle);
   }

   return getTimesOfDayCycleDailyDose(cycle) * getCycleAdministrationDays(cycle);
};

export const getExtendedDosageTreatmentDaysCount = (extendedDosage: ExtendedDosage | undefined): number | undefined => {
   if (!extendedDosage?.stages.length) {
      return undefined;
   }

   return extendedDosage.stages.reduce((sum, stage) => sum + getStageDurationDays(stage.duration), 0);
};

export const isExtendedDosageExceedingYearLimit = (extendedDosage: ExtendedDosage | undefined): boolean => {
   const treatmentDaysCount = getExtendedDosageTreatmentDaysCount(extendedDosage);

   return (
      treatmentDaysCount !== undefined && treatmentDaysCount > getTreatmentDaysCountMaxValue({ issuedForYear: true })
   );
};

export const getExtendedDosageMedicineDemand = (extendedDosage: ExtendedDosage | undefined): number | undefined => {
   if (!extendedDosage?.stages.length) {
      return undefined;
   }

   return extendedDosage.stages.filter(isDosageCycle).reduce((sum, cycle) => sum + getCycleMedicineDemand(cycle), 0);
};

export const getExtendedDosageAdministrationDaysCount = (
   extendedDosage: ExtendedDosage | undefined
): number | undefined => {
   if (!extendedDosage?.stages.length) {
      return undefined;
   }

   return extendedDosage.stages
      .filter(isDosageCycle)
      .reduce((sum, cycle) => sum + getCycleAdministrationDays(cycle), 0);
};

const getSinglePackageCapacity = (medicine: MedicineSimple | undefined): number | undefined => {
   if (!hasKnownPackageCapacity(medicine) || hasCompositePackage(medicine)) {
      return undefined;
   }

   return medicine?.singlePackageCapacity || undefined;
};

export const getExtendedDosagePackagesCount = (
   extendedDosage: ExtendedDosage | undefined,
   medicine: MedicineSimple | undefined
): number | undefined => {
   const demand = getExtendedDosageMedicineDemand(extendedDosage);
   const singlePackageCapacity = getSinglePackageCapacity(medicine);

   if (!demand || !singlePackageCapacity) {
      return undefined;
   }

   return Math.ceil(demand / singlePackageCapacity);
};

export const getExceededExtendedDosagePackagesCountByStage = (
   extendedDosage: ExtendedDosage | undefined,
   medicine: MedicineSimple | undefined
): Map<number, number> => {
   const singlePackageCapacity = getSinglePackageCapacity(medicine);

   if (!extendedDosage || !singlePackageCapacity) {
      return new Map();
   }

   return new Map(
      extendedDosage.stages.flatMap((stage, index): [number, number][] => {
         if (!isDosageCycle(stage)) {
            return [];
         }

         const packagesCount = Math.ceil(getCycleMedicineDemand(stage) / singlePackageCapacity);

         return packagesCount > getQuantityMaxValue() ? [[index, packagesCount]] : [];
      })
   );
};

export const getExceededExtendedDosagePackagesCount = (
   extendedDosage: ExtendedDosage | undefined,
   medicine: MedicineSimple | undefined
): number | undefined => {
   const packagesCount = getExtendedDosagePackagesCount(extendedDosage, medicine);

   return packagesCount !== undefined && packagesCount > getQuantityMaxValue() ? packagesCount : undefined;
};

/*
 * Hour intervals must split the day equally (like 1, 2, 3, 4, 6, 8, 12, 24x per day in precise dosage);
 * whole days written in hours (48, 72) are fine, other units are never restricted
 */
export const isHourIntervalAllowed = (hours: number): boolean => {
   return HOURS_PER_DAY % hours === 0 || hours % HOURS_PER_DAY === 0;
};

export const isDosageIntervalAllowed = (interval: DosagePeriod): boolean => {
   if (interval.unit !== 'HOUR') {
      return true;
   }

   return isHourIntervalAllowed(Number(interval.amount));
};

const isFrequencyNotAllowed = (stage: DosageStageUnion): boolean => {
   return isFrequencyDosageCycle(stage) && !isDosageIntervalAllowed(stage.interval);
};

export const hasNotAllowedDosageFrequency = (stages: DosageStageUnion[]): boolean => {
   return stages.some(isFrequencyNotAllowed);
};

const getCycleIntervalInHours = (cycle: FrequencyDosageCycle | TimesOfDayDosageCycle): number => {
   return isFrequencyDosageCycle(cycle)
      ? getPeriodInHours(cycle.interval)
      : Number(cycle.administrationIntervalDays) * HOURS_PER_DAY;
};

const isDurationNotMultipleOfInterval = (stage: DosageStageUnion): boolean => {
   if (!isDosageCycle(stage) || (isFrequencyDosageCycle(stage) && stage.interval.unit === 'HOUR')) {
      return false;
   }

   const intervalHours = getCycleIntervalInHours(stage);

   return intervalHours > 0 && getPeriodInHours(stage.duration) % intervalHours !== 0;
};

const getCycleIntervalAmount = (cycle: FrequencyDosageCycle | TimesOfDayDosageCycle): number => {
   return isFrequencyDosageCycle(cycle) ? Number(cycle.interval.amount) : Number(cycle.administrationIntervalDays);
};

export type DosageStageValidation = {
   frequencyNotAllowed: boolean;
   durationNotMultipleOfIntervalAmount: number | undefined;
   exceededPackagesCount: number | undefined;
};

export const NO_DOSAGE_STAGE_VALIDATION: DosageStageValidation = {
   frequencyNotAllowed: false,
   durationNotMultipleOfIntervalAmount: undefined,
   exceededPackagesCount: undefined,
};

export const getDosageStageValidations = (options: {
   stages: DosageStageUnion[];
   dosageFrequencyValidated: boolean;
   exceededPackagesCountByStage: Map<number, number>;
}): DosageStageValidation[] => {
   return options.stages.map((stage, index) => ({
      frequencyNotAllowed: options.dosageFrequencyValidated && isFrequencyNotAllowed(stage),
      durationNotMultipleOfIntervalAmount:
         isDosageCycle(stage) && isDurationNotMultipleOfInterval(stage) ? getCycleIntervalAmount(stage) : undefined,
      exceededPackagesCount: options.exceededPackagesCountByStage.get(index),
   }));
};

export const hasDosageStageValidationError = (validations: DosageStageValidation[]): boolean => {
   return validations.some(
      (validation) => validation.frequencyNotAllowed || validation.durationNotMultipleOfIntervalAmount !== undefined
   );
};

export const hasMisplacedDosageBreak = (stages: Pick<DosageStage, 'class'>[]): boolean => {
   return stages.some((stage, index) => {
      if (stage.class !== 'DosageBreak') {
         return false;
      }

      const previousStage = stages[index - 1];
      const nextStage = stages[index + 1];

      return !previousStage || previousStage.class === 'DosageBreak' || !nextStage || nextStage.class === 'DosageBreak';
   });
};

export const hasZeroDoseCycle = (stages: Pick<DosageStageUnion, 'class'>[], portions: (number | null)[][]): boolean => {
   return stages.some(
      (stage, index) =>
         stage.class === 'TimesOfDayDosageCycle' && (portions[index] ?? []).every((portion) => !Number(portion ?? 0))
   );
};

export const isDosageBreakAppendable = (stages: Pick<DosageStage, 'class'>[]): boolean => {
   const lastStage = stages[stages.length - 1];

   if (!lastStage) {
      return false;
   }

   return lastStage.class !== 'DosageBreak';
};

export const isDosageStageDuplicable = (stages: Pick<DosageStage, 'class'>[], index: number): boolean => {
   const stage = stages[index];

   if (!stage || stage.class !== 'DosageBreak') {
      return true;
   }

   return isDosageBreakAppendable(stages);
};
